import React, { useState } from 'react';
import { ZoomIn, ZoomOut, RotateCw, Download, Maximize2, Edit2, Save, Image, Info } from 'lucide-react';

interface ImagePreviewProps {
  asset: {
    id: number;
    file_name: string;
    metadata?: any;
    content?: any;
  };
  apiBaseUrl: string;
  onSave?: (assetId: number, content: string) => Promise<void>;
}

export default function ImagePreview({ asset, apiBaseUrl, onSave }: ImagePreviewProps) {
  // Parse metadata
  const parseMetadata = () => {
    if (asset.metadata) {
      if (typeof asset.metadata === 'string') {
        try {
          return JSON.parse(asset.metadata);
        } catch (e) {
          return {};
        }
      }
      return asset.metadata;
    }
    return {};
  };

  const initialMetadata = parseMetadata();

  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [metadata, setMetadata] = useState({
    alt_text: initialMetadata.alt_text || '',
    title: initialMetadata.title || asset.file_name,
    description: initialMetadata.description || '',
    caption: initialMetadata.caption || '',
    photographer: initialMetadata.photographer || '',
    copyright: initialMetadata.copyright || '',
    tags: Array.isArray(initialMetadata.tags) ? initialMetadata.tags.join(', ') : '',
    url: initialMetadata.url || ''
  });

  // Get asset URL
  const getAssetUrl = () => {
    if (metadata.url) {
      return metadata.url;
    }
    if (initialMetadata.url) {
      return initialMetadata.url;
    }
    return `${apiBaseUrl}/assets/${asset.id}/download`;
  };

  const assetUrl = getAssetUrl();

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = assetUrl;
    link.download = asset.file_name;
    link.click();
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleSave = async () => {
    if (!onSave) return;

    setIsSaving(true);
    try {
      const updatedMetadata = {
        ...initialMetadata,
        ...metadata,
        tags: metadata.tags.split(',').map(t => t.trim()).filter(t => t)
      };
      await onSave(asset.id, JSON.stringify(updatedMetadata));
      setIsEditMode(false);
    } catch (error) {
      console.error('Failed to save metadata:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header Toolbar */}
      <div className="bg-white border-b px-4 py-2 pr-64">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Image className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Image Preview</h2>
          </div>

          <div className="flex items-center space-x-2">
            {/* Zoom Controls */}
            <button
              onClick={handleZoomOut}
              className="p-2 hover:bg-gray-100 rounded"
              title="Zoom Out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-600 min-w-[60px] text-center">
              {Math.round(zoom * 100)}%
            </span>
            <button
              onClick={handleZoomIn}
              className="p-2 hover:bg-gray-100 rounded"
              title="Zoom In"
            >
              <ZoomIn className="w-4 h-4" />
            </button>

            <div className="w-px h-6 bg-gray-300 mx-2" />

            {/* Other Controls */}
            <button
              onClick={handleRotate}
              className="p-2 hover:bg-gray-100 rounded"
              title="Rotate"
            >
              <RotateCw className="w-4 h-4" />
            </button>
            <button
              onClick={toggleFullscreen}
              className="p-2 hover:bg-gray-100 rounded"
              title="Fullscreen"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleDownload}
              className="p-2 hover:bg-gray-100 rounded"
              title="Download"
            >
              <Download className="w-4 h-4" />
            </button>

            <div className="w-px h-6 bg-gray-300 mx-2" />

            {/* Edit Mode Toggle */}
            <button
              onClick={() => isEditMode ? handleSave() : setIsEditMode(true)}
              disabled={isSaving}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
            >
              {isEditMode ? (
                <>
                  <Save className="w-4 h-4" />
                  <span>Save</span>
                </>
              ) : (
                <>
                  <Edit2 className="w-4 h-4" />
                  <span>Edit Info</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Image Display Area */}
        <div className="flex-1 overflow-auto p-4">
          <div className="min-h-full flex items-center justify-center">
            <img
              src={assetUrl}
              alt={metadata.alt_text || asset.file_name}
              title={metadata.title}
              className="max-w-full shadow-lg rounded-lg transition-transform duration-200"
              style={{
                transform: `scale(${zoom}) rotate(${rotation}deg)`,
                transformOrigin: 'center',
              }}
              onError={(e) => {
                e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHRleHQtYW5jaG9yPSJtaWRkbGUiIHg9IjIwMCIgeT0iMTUwIiBzdHlsZT0iZmlsbDojYWFhO2ZvbnQtd2VpZ2h0OmJvbGQ7Zm9udC1zaXplOjI1cHg7Zm9udC1mYW1pbHk6QXJpYWwsc2Fucy1zZXJpZiI+SW1hZ2UgUHJldmlldyBVbmF2YWlsYWJsZTwvdGV4dD48L3N2Zz4=';
              }}
            />
          </div>
        </div>

        {/* Metadata Panel */}
        <div className="w-96 bg-white border-l overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Info className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold">Image Information</h3>
            </div>

            <div className="space-y-4">
              {/* Alt Text - Most Important for SEO */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alt Text (SEO) *
                </label>
                {isEditMode ? (
                  <textarea
                    value={metadata.alt_text}
                    onChange={(e) => setMetadata({ ...metadata, alt_text: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-20"
                    placeholder="Describe the image for screen readers and SEO..."
                  />
                ) : (
                  <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded">
                    {metadata.alt_text || 'No alt text provided'}
                  </p>
                )}
                {isEditMode && (
                  <p className="text-xs text-gray-500 mt-1">
                    Describe what's in the image. This is crucial for accessibility and SEO.
                  </p>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                {isEditMode ? (
                  <input
                    type="text"
                    value={metadata.title}
                    onChange={(e) => setMetadata({ ...metadata, title: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Image title..."
                  />
                ) : (
                  <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded">
                    {metadata.title}
                  </p>
                )}
              </div>

              {/* Caption */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Caption
                </label>
                {isEditMode ? (
                  <textarea
                    value={metadata.caption}
                    onChange={(e) => setMetadata({ ...metadata, caption: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-16"
                    placeholder="Image caption for display..."
                  />
                ) : (
                  <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded">
                    {metadata.caption || 'No caption'}
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                {isEditMode ? (
                  <textarea
                    value={metadata.description}
                    onChange={(e) => setMetadata({ ...metadata, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-16"
                    placeholder="Detailed description..."
                  />
                ) : (
                  <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded">
                    {metadata.description || 'No description'}
                  </p>
                )}
              </div>

              {/* Photographer/Credit */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Photographer/Credit
                </label>
                {isEditMode ? (
                  <input
                    type="text"
                    value={metadata.photographer}
                    onChange={(e) => setMetadata({ ...metadata, photographer: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Photo credit..."
                  />
                ) : (
                  <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded">
                    {metadata.photographer || 'Not specified'}
                  </p>
                )}
              </div>

              {/* Copyright */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Copyright
                </label>
                {isEditMode ? (
                  <input
                    type="text"
                    value={metadata.copyright}
                    onChange={(e) => setMetadata({ ...metadata, copyright: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="© 2024 Company Name"
                  />
                ) : (
                  <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded">
                    {metadata.copyright || 'Not specified'}
                  </p>
                )}
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags (comma-separated)
                </label>
                {isEditMode ? (
                  <input
                    type="text"
                    value={metadata.tags}
                    onChange={(e) => setMetadata({ ...metadata, tags: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="tag1, tag2, tag3"
                  />
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {metadata.tags ? (
                      metadata.tags.split(',').map((tag, i) => (
                        <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                          {tag.trim()}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-500">No tags</span>
                    )}
                  </div>
                )}
              </div>

              {/* Image URL (if external) */}
              {metadata.url && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Image URL
                  </label>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={metadata.url}
                      onChange={(e) => setMetadata({ ...metadata, url: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="https://..."
                    />
                  ) : (
                    <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded text-sm break-all">
                      {metadata.url}
                    </p>
                  )}
                </div>
              )}

              {/* Technical Details (Read-only) */}
              <div className="pt-4 border-t">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Technical Details</h4>
                <div className="space-y-1 text-sm text-gray-600">
                  <p>Filename: {asset.file_name}</p>
                  {initialMetadata.dimensions && (
                    <p>Dimensions: {initialMetadata.dimensions.width} × {initialMetadata.dimensions.height}px</p>
                  )}
                  {initialMetadata.file_size && (
                    <p>Size: {(initialMetadata.file_size / 1024).toFixed(1)} KB</p>
                  )}
                  {initialMetadata.mime_type && (
                    <p>Type: {initialMetadata.mime_type}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Save Button (when in edit mode) */}
            {isEditMode && (
              <div className="mt-6 flex justify-end space-x-2">
                <button
                  onClick={() => setIsEditMode(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
          onClick={toggleFullscreen}
        >
          <img
            src={assetUrl}
            alt={metadata.alt_text || asset.file_name}
            className="max-w-full max-h-full"
            style={{
              transform: `rotate(${rotation}deg)`,
            }}
          />
        </div>
      )}
    </div>
  );
}